import { isMapId, mapInfo } from "@69ly/maps";
import type { MatchNote, NoteSide } from "@69ly/shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { Icon } from "../components/Icon.tsx";
import { Select } from "../components/Select.tsx";
import { mapIconUrl } from "../lib/map-art.ts";
import { NoteTags } from "./NoteFields.tsx";

const mapName = (map: string) => (isMapId(map) ? mapInfo(map).name : map);
const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
/** Where a note opens: its round, a second before its moment. */
const noteUrl = (n: MatchNote) => `/demos/${n.demoId}?r=${n.round + 1}${n.seconds !== null ? `&t=${Math.max(0, n.seconds - 1).toFixed(1)}` : `&tk=${n.tick}`}`;

interface Filters {
  map: string;
  author: string;
  team: string;
  side: NoteSide | "";
  q: string;
}
const NONE: Filters = { map: "", author: "", team: "", side: "", q: "" };

/**
 * Notes, filtered by map, who wrote them, the team and side they are about, and words
 * (in the note, its player, its team): the match page's notes.
 */
export function NotesBrowser({ notes }: { notes: MatchNote[] }) {
  const { t } = useTranslation();
  const [f, setF] = useState<Filters>(NONE);
  const set = (change: Partial<Filters>) => setF((x) => ({ ...x, ...change }));

  const maps = useMemo(() => [...new Set(notes.map((n) => n.map))].sort((a, b) => mapName(a).localeCompare(mapName(b))), [notes]);
  const authors = useMemo(() => [...new Map(notes.flatMap((n) => (n.author ? [[n.author.id, n.author] as const] : []))).values()].sort((a, b) => a.name.localeCompare(b.name)), [notes]);
  // Teams by name: the same opponent across matches is one choice.
  const teams = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const n of notes) if (n.team?.name) byKey.set(n.team.name.trim().toLowerCase(), byKey.get(n.team.name.trim().toLowerCase()) ?? n.team.name);
    return [...byKey.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [notes]);
  const q = f.q.trim().toLowerCase();
  const shown = notes.filter(
    (n) =>
      (!f.map || n.map === f.map) &&
      (!f.author || n.author?.id === f.author) &&
      (!f.team || n.team?.name.trim().toLowerCase() === f.team) &&
      (!f.side || n.side === f.side) &&
      (!q || `${n.text} ${n.playerName ?? ""} ${n.team?.name ?? ""}`.toLowerCase().includes(q)),
  );
  const filtered = f.map || f.author || f.team || f.side || q;

  const manyMaps = new Set(notes.map((n) => n.mapNumber)).size > 1;

  return (
    <div className="notes-browser">
      <div className="match-filters notes-filters" role="group" aria-label={t("notes.filters")}>
        {maps.length > 1 && (
          <Select
            label={t("matches.filters.map")}
            value={f.map}
            placeholder={t("demos.allMaps")}
            groups={[
              {
                options: [
                  { value: "", label: t("demos.allMaps") },
                  ...maps.map((m) => ({ value: m, label: mapName(m), icon: isMapId(m) ? <img src={mapIconUrl(m)} alt="" width={18} height={18} /> : undefined })),
                ],
              },
            ]}
            onChange={(v) => set({ map: v })}
          />
        )}
        {authors.length > 1 && (
          <Select
            label={t("notes.byAuthor")}
            value={f.author}
            placeholder={t("notes.anyAuthor")}
            groups={[{ options: [{ value: "", label: t("notes.anyAuthor") }, ...authors.map((a) => ({ value: a.id, label: a.name }))] }]}
            onChange={(v) => set({ author: v })}
          />
        )}
        {teams.length > 0 && (
          <Select
            label={t("notes.aboutWhichTeam")}
            value={f.team}
            placeholder={t("notes.anyTeam")}
            groups={[{ options: [{ value: "", label: t("notes.anyTeam") }, ...teams.map(([key, name]) => ({ value: key, label: name }))] }]}
            onChange={(v) => set({ team: v })}
          />
        )}
        <div className="segmented segmented-small" role="group" aria-label={t("notes.side")}>
          {(["", "t", "ct"] as const).map((s) => (
            <button key={s || "any"} type="button" className={`seg${s ? ` side-seg-${s}` : ""}`} aria-pressed={f.side === s} onClick={() => set({ side: s })}>
              {s ? t(`live.sideShort.${s}`) : t("notes.anySide")}
            </button>
          ))}
        </div>
        <label className="match-search">
          <span className="visually-hidden">{t("notes.search")}</span>
          <Icon name="search" />
          <input type="search" value={f.q} placeholder={t("notes.search")} onChange={(e) => set({ q: e.target.value })} />
        </label>
      </div>

      <p className="muted small" role="status">
        {t("notes.shown", { count: shown.length })}
        {filtered && (
          <>
            {" · "}
            <button type="button" className="link-button" onClick={() => setF(NONE)}>
              {t("mapView.clearFilters")}
            </button>
          </>
        )}
      </p>

      <ul className="notes-rows">
        {shown.map((n) => (
          <li key={n.id} className="notes-row">
            <Link to={noteUrl(n)} className="notes-row-go" aria-label={t("notes.open", { round: n.round + 1, text: n.text })}>
              <span className="notes-row-where mono small">
                {manyMaps && isMapId(n.map) && <img src={mapIconUrl(n.map)} alt="" width={16} height={16} />}
                {manyMaps && <span>{mapName(n.map)}</span>}
                <span>{t("notes.at", { n: n.round + 1, clock: n.seconds !== null ? clock(n.seconds) : "·" })}</span>
              </span>
              <span className="notes-row-text">{n.text}</span>
              {n.pin && (
                <span className="chip chip-small match-note-pin">
                  <Icon name={n.pin.kind === "player" ? "user" : "target"} size={12} />
                  {n.pin.kind === "player" ? (n.playerName ?? t("matches.notes.aPlayer")) : t("matches.notes.aSpot")}
                </span>
              )}
              <Icon name="play" size={14} />
            </Link>
            <div className="notes-row-meta">
              <NoteTags side={n.side} team={n.team} links={n.links} />
              {n.author && (
                <span className="match-note-author muted small">
                  <Avatar name={n.author.name} url={n.author.avatarUrl} size={18} />
                  {n.author.name}
                  {n.editedAt && <span> · {t("notes.edited")}</span>}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
